/**
 * PDP Product Section – custom element
 *
 * Enhances the server-rendered product section with:
 * - Card picker interactions (bundle or stepped mode)
 * - Feature dimming based on selected model
 * - Thumbnail gallery navigation
 * - Form input updates (variant ID + selling plan)
 *
 * Supports multiple instances on the same page.
 */

if (!customElements.get('pdp-product-section')) {
  console.log('[Whoop] pdp-product-section.js loaded');
  class PdpProductSection extends HTMLElement {
    constructor() {
      super();
      console.log('[Whoop] PdpProductSection initialized');
      this._selectedModel = 'fit';
      this._selectedPlan = 'yearly';
    }

    connectedCallback() {
      const configEl = this.querySelector('[data-pdp-config]');
      if (!configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        console.error('[PdpProductSection] Bad config JSON', e);
        return;
      }

      this._cacheElements();
      this._bindCardPickers();
      this._bindGallery();
      this._bindFormLoading();
      this._bindCompareModal();
      this._updateFeatureDimming();

      // Stepped mode: subscription variant is pre-set in the form.
      // Enable the id input so it's included in FormData on submit.
      if (this.config.pricingMode === 'stepped' && this._variantInput) {
        this._variantInput.disabled = false;
      }

      this.classList.add('is-initialized');
    }

    // ================================================================
    //  ELEMENT CACHE
    // ================================================================

    _cacheElements() {
      this._productForm = this.querySelector('product-form');
      this._form = this.querySelector('[data-type="add-to-cart-form"]');
      this._variantInput = this._form
        ? this._form.querySelector('input[name="id"]')
        : null;
      this._sellingPlanInput = this._form
        ? this._form.querySelector('input[name="selling_plan"]')
        : null;
      this._planTypeInput = this._form
        ? this._form.querySelector('[data-plan-type-input]')
        : null;
      this._altVariantInput = this._form
        ? this._form.querySelector('[data-alt-variant-input]')
        : null;
      this._altSpInput = this._form
        ? this._form.querySelector('[data-alt-sp-input]')
        : null;
      this._altProductInput = this._form
        ? this._form.querySelector('[data-alt-product-input]')
        : null;
      this._submitBtn = this._form
        ? this._form.querySelector('[name="add"]')
        : null;
      this._slider = this.querySelector('[data-gallery-slider]');
      this._thumbs = this.querySelectorAll('.pdp-gallery__thumb:not(.pdp-gallery__thumb--hidden)');
      this._prevBtn = this.querySelector('[data-gallery-prev]');
      this._nextBtn = this.querySelector('[data-gallery-next]');
      this._slides = this._slider
        ? this._slider.querySelectorAll('.pdp-gallery__slide:not(.pdp-gallery__slide--hidden)')
        : [];
      this._activeSlide = 0;
      this._featureItems = this.querySelectorAll('.pdp-feature-item[data-variant]');
    }

    // ================================================================
    //  CARD PICKER BINDING
    // ================================================================

    _bindCardPickers() {
      const mode = this.config.pricingMode;

      if (mode === 'bundle') {
        this._bindBundleCards();
      } else if (mode === 'stepped') {
        this._bindDeviceCards();
        this._bindSubCards();
      }
    }

    /** Bundle mode: .pdp-variant-card */
    _bindBundleCards() {
      const cards = this.querySelectorAll('[data-variant-card]');
      cards.forEach((card) => {
        card.addEventListener('click', (e) => {
          // Don't double-fire if clicking the radio itself
          if (e.target.tagName === 'INPUT') return;
          const radio = card.querySelector('input[type="radio"]');
          if (radio) radio.checked = true;
          this._onBundleSelect(card);
        });
        // Also listen for radio change (keyboard)
        const radio = card.querySelector('input[type="radio"]');
        if (radio) {
          radio.addEventListener('change', () => {
            if (radio.checked) this._onBundleSelect(card);
          });
        }
      });
    }

    /**
     * Bundle mode (Variant A): user picks a device card.
     * - Sets the form variant to the subscription variant (fit or lite)
     * - Dispatches event with the selected variant ID
     *
     * Cart result: subscription + bonus items (from metafield) — all submitted in one request
     */
    _onBundleSelect(card) {
      const variantId = card.dataset.variantId;
      const model = card.dataset.model;

      this.querySelectorAll('[data-variant-card]').forEach((c) =>
        c.classList.toggle('is-selected', c === card)
      );

      this._selectedModel = model;
      this._setVariantId(variantId);

      // Update selling plan to match selected model (bundle mode)
      const sellingPlanId = model === 'lite'
        ? this.config.liteSellingPlanId
        : this.config.fitSellingPlanId;
      this._setSellingPlan(sellingPlanId);

      this._updateFeatureDimming();
      this._updateBonusItems();
      this._updateGallery(model);

      document.dispatchEvent(new CustomEvent('pdp:device-selected', {
        detail: { variantId: variantId, model: model }
      }));
    }

    /** Stepped mode Step 1: .pdp-device-card */
    _bindDeviceCards() {
      const cards = this.querySelectorAll('[data-device-card]');
      cards.forEach((card) => {
        card.addEventListener('click', (e) => {
          if (e.target.tagName === 'INPUT') return;
          const radio = card.querySelector('input[type="radio"]');
          if (radio) radio.checked = true;
          this._onDeviceSelect(card);
        });
        const radio = card.querySelector('input[type="radio"]');
        if (radio) {
          radio.addEventListener('change', () => {
            if (radio.checked) this._onDeviceSelect(card);
          });
        }
      });
    }

    /**
     * Stepped mode (Variant B) Step 1: device selection.
     * Stores device model for addon items (device is added as addon, not primary).
     * Subscription remains the form's primary item.
     *
     * Cart result: Subscription (primary) + FIT/Lite (addon) + bonus items
     */
    _onDeviceSelect(card) {
      const variantId = card.dataset.variantId;
      const model = card.dataset.model;

      this.querySelectorAll('[data-device-card]').forEach((c) =>
        c.classList.toggle('is-selected', c === card)
      );

      this._selectedModel = model;
      this._updateFeatureDimming();
      this._updateBonusItems();
      this._updateAddonItems();
      this._updateGallery(model);


      document.dispatchEvent(new CustomEvent('pdp:device-selected', {
        detail: { variantId: variantId, model: model }
      }));
    }

    /** Stepped mode Step 2: .pdp-sub-card */
    _bindSubCards() {
      const cards = this.querySelectorAll('[data-sub-card]');
      cards.forEach((card) => {
        card.addEventListener('click', (e) => {
          if (e.target.tagName === 'INPUT') return;
          const radio = card.querySelector('input[type="radio"]');
          if (radio) radio.checked = true;
          this._onSubSelect(card);
        });
        const radio = card.querySelector('input[type="radio"]');
        if (radio) {
          radio.addEventListener('change', () => {
            if (radio.checked) this._onSubSelect(card);
          });
        }
      });
    }

    /**
     * Stepped mode (Variant B) Step 2: subscription plan selection.
     * Sets the form variant + selling plan to the selected subscription.
     * Updates plan properties (for cart upsell) and device addon items.
     */
    _onSubSelect(card) {
      const plan = card.dataset.plan;

      this.querySelectorAll('[data-sub-card]').forEach((c) =>
        c.classList.toggle('is-selected', c === card)
      );

      this._selectedPlan = plan;

      // Set form to subscription variant + selling plan
      const plans = {
        yearly:    { variantId: this.config.yearlyVariantId,    sellingPlanId: this.config.yearlySellingPlanId,    productId: this.config.yearlyProductId },
        quarterly: { variantId: this.config.quarterlyVariantId, sellingPlanId: this.config.quarterlySellingPlanId, productId: this.config.quarterlyProductId },
        monthly:   { variantId: this.config.monthlyVariantId,   sellingPlanId: this.config.monthlySellingPlanId,   productId: this.config.monthlyProductId }
      };
      const selected = plans[plan];
      if (selected) {
        this._setVariantId(selected.variantId);
        this._setSellingPlan(selected.sellingPlanId);
      }

      // Update plan properties for cart upsell
      const altKey = plan !== 'yearly' ? 'yearly' : 'monthly';
      const alt = plans[altKey] || {};
      if (this._planTypeInput) this._planTypeInput.value = plan;
      if (this._altVariantInput) this._altVariantInput.value = alt.variantId || '';
      if (this._altSpInput) this._altSpInput.value = alt.sellingPlanId || '';
      if (this._altProductInput) this._altProductInput.value = alt.productId || '';

      this._updateAddonItems();
      this._updateBonusItems();

    }

    // ================================================================
    //  FORM INPUT UPDATES
    // ================================================================

    _setVariantId(id) {
      if (!this._variantInput) return;
      this._variantInput.value = id;
      // Enable the input (Shopify's product-form pattern disables it initially)
      this._variantInput.disabled = false;

      // Update button availability
      const variant = this.config.variants.find(
        (v) => String(v.id) === String(id)
      );
      if (this._submitBtn && variant) {
        this._submitBtn.disabled = !variant.available;
        this._submitBtn.classList.toggle('is-sold-out', !variant.available);
        const textEl = this._submitBtn.querySelector('.pdp-cta__button-text');
        if (textEl && !variant.available) {
          textEl.textContent = this._submitBtn.closest('pdp-product-section')
            ? 'Sold out'
            : textEl.textContent;
        }
      }
    }

    _setSellingPlan(id) {
      if (!this._sellingPlanInput) return;
      this._sellingPlanInput.value = id || '';
    }

    /**
     * Build and set data-addon-items on product-form.
     * In stepped mode the DEVICE is the addon (subscription is the primary form item).
     */
    _updateAddonItems() {
      if (!this._productForm || this.config.pricingMode !== 'stepped') return;

      const deviceVariantId = this._selectedModel === 'lite'
        ? this.config.liteVariantId
        : this.config.fitVariantId;
      const deviceProductId = this._selectedModel === 'lite'
        ? this.config.liteProductId
        : this.config.fitProductId;

      if (!deviceVariantId) {
        this._productForm.dataset.addonItems = '[]';
        return;
      }

      const deviceSellingPlanId = this._selectedModel === 'lite'
        ? this.config.steppedLiteSellingPlanId
        : this.config.steppedFitSellingPlanId;

      var addonObj = {
        variant_id: deviceVariantId,
        product_id: deviceProductId
      };
      if (deviceSellingPlanId) addonObj.selling_plan_id = deviceSellingPlanId;

      this._productForm.dataset.addonItems = JSON.stringify([addonObj]);
    }

    /**
     * Swap data-bonus-items on the product-form to match the selected model.
     * Also refreshes the BonusItemsManager config cache so the next add-to-cart
     * uses the correct bonus products.
     */
    _updateBonusItems() {
      if (!this._productForm) return;

      const deviceBonus = this._selectedModel === 'lite'
        ? this.config.bonusLite
        : this.config.bonusFit;

      // In stepped mode, merge device bonus items with subscription bonus items
      var merged = [].concat(deviceBonus || []);
      if (this.config.pricingMode === 'stepped') {
        var subBonusKey = 'bonus' + this._selectedPlan.charAt(0).toUpperCase() + this._selectedPlan.slice(1) + 'Sub';
        var subBonus = this.config[subBonusKey] || [];
        merged = merged.concat(subBonus);
      }

      this._productForm.dataset.bonusItems = JSON.stringify(merged);

      if (window.bonusItemsManager) {
        window.bonusItemsManager._scanDOMForConfig();
      }
    }

    // ================================================================
    //  FORM LOADING STATE
    // ================================================================

    _bindFormLoading() {
      if (!this._form || !this._submitBtn) return;

      this._successTimeout = null;

      this._form.addEventListener('submit', () => {
        // Clear any lingering success state from a previous add
        if (this._successTimeout) {
          clearTimeout(this._successTimeout);
          this._successTimeout = null;
        }
        this._submitBtn.classList.remove('is-success');
        this._submitBtn.classList.add('is-loading');
        this._submitBtn.setAttribute('aria-disabled', 'true');
      });

      const showSuccess = () => {
        // Transition: loading → success
        this._submitBtn.classList.remove('is-loading');
        this._submitBtn.classList.add('is-success');

        // Transition: success → default after 2s
        this._successTimeout = setTimeout(() => {
          this._submitBtn.classList.remove('is-success');
          this._submitBtn.removeAttribute('aria-disabled');
          this._successTimeout = null;
        }, 2000);
      };

      const resetLoading = () => {
        this._submitBtn.classList.remove('is-loading', 'is-success');
        this._submitBtn.removeAttribute('aria-disabled');
      };

      document.addEventListener('cart:item-added', showSuccess);
      document.addEventListener('product:variant-error', resetLoading);
    }

    // ================================================================
    //  COMPARE TABLE MODAL
    // ================================================================

    _bindCompareModal() {
      const triggers = this.querySelectorAll('[data-open-compare-modal]');
      const popup = this.querySelector('[data-compare-popup]');
      if (triggers.length === 0 || !popup) return;

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          popup.classList.remove('hidden');
        });
      });

      const closeBtn = popup.querySelector('.pdp-compare-popup__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          popup.classList.add('hidden');
        });
      }

      const overlay = popup.querySelector('.pdp-compare-popup__overlay');
      if (overlay) {
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            popup.classList.add('hidden');
          }
        });
      }
    }

    // ================================================================
    //  FEATURE DIMMING
    // ================================================================

    _updateFeatureDimming() {
      this._featureItems.forEach((item) => {
        const scope = item.dataset.variant;
        if (scope === 'all') {
          item.classList.remove('is-dimmed');
        } else if (scope === 'fit') {
          item.classList.toggle('is-dimmed', this._selectedModel !== 'fit');
        }
      });
    }

    // ================================================================
    //  GALLERY (scroll-snap slider + thumbnails + arrows)
    // ================================================================

    _bindGallery() {
      if (!this._slider || this._slides.length < 2) return;

      // Bind thumbnail clicks to ALL thumbs (including hidden model thumbs)
      // so they work after _updateGallery swaps visibility.
      const allThumbs = this.querySelectorAll('.pdp-gallery__thumb');
      allThumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const idx = parseInt(thumb.dataset.thumbIndex, 10);
          this._scrollToSlide(idx);
        });
      });

      // Arrow clicks
      if (this._prevBtn) {
        this._prevBtn.addEventListener('click', () => {
          this._scrollToSlide(Math.max(0, this._activeSlide - 1));
        });
      }
      if (this._nextBtn) {
        this._nextBtn.addEventListener('click', () => {
          this._scrollToSlide(
            Math.min(this._slides.length - 1, this._activeSlide + 1)
          );
        });
      }

      // Observe scroll position → update active thumbnail
      this._scrollObserver = null;
      if ('IntersectionObserver' in window) {
        this._scrollObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const idx = parseInt(
                  entry.target.dataset.slideIndex,
                  10
                );
                this._activeSlide = idx;
                this._setActiveThumb(idx);
              }
            });
          },
          { root: this._slider, threshold: 0.6 }
        );
        this._slides.forEach((slide) =>
          this._scrollObserver.observe(slide)
        );
      }
    }

    /**
     * Toggle gallery slides & thumbs to show only the selected model's media.
     * Re-indexes visible slides so arrows and observer work correctly.
     */
    _updateGallery(model) {
      if (!this._slider) return;

      const allSlides = this._slider.querySelectorAll('.pdp-gallery__slide');
      const allThumbs = this.querySelectorAll('.pdp-gallery__thumb');

      // Toggle visibility
      allSlides.forEach((slide) => {
        slide.classList.toggle(
          'pdp-gallery__slide--hidden',
          slide.dataset.model && slide.dataset.model !== model
        );
      });
      allThumbs.forEach((thumb) => {
        thumb.classList.toggle(
          'pdp-gallery__thumb--hidden',
          thumb.dataset.model && thumb.dataset.model !== model
        );
      });

      // Re-cache only visible slides and thumbs
      this._slides = this._slider.querySelectorAll(
        '.pdp-gallery__slide:not(.pdp-gallery__slide--hidden)'
      );
      this._thumbs = this.querySelectorAll(
        '.pdp-gallery__thumb:not(.pdp-gallery__thumb--hidden)'
      );

      // Re-index visible slides/thumbs so arrow navigation stays sequential
      this._slides.forEach((slide, i) => {
        slide.dataset.slideIndex = i;
      });
      this._thumbs.forEach((thumb, i) => {
        thumb.dataset.thumbIndex = i;
        thumb.setAttribute('aria-label', 'Go to image ' + (i + 1));
      });

      // Reconnect IntersectionObserver for the new visible slides
      if (this._scrollObserver) {
        this._scrollObserver.disconnect();
        this._slides.forEach((slide) =>
          this._scrollObserver.observe(slide)
        );
      }

      // Reset to first slide
      this._scrollToSlide(0);
      this._activeSlide = 0;
      this._setActiveThumb(0);
    }

    _scrollToSlide(index) {
      const slide = this._slides[index];
      if (!slide || !this._slider) return;
      this._slider.scrollTo({
        left: slide.offsetLeft,
        behavior: 'smooth',
      });
    }

    _setActiveThumb(index) {
      this._thumbs.forEach((thumb) => {
        thumb.classList.toggle(
          'is-active',
          parseInt(thumb.dataset.thumbIndex, 10) === index
        );
      });
      // Scroll the active thumbnail into view
      const activeThumb = this._thumbs[index];
      if (activeThumb) {
        activeThumb.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }

  customElements.define('pdp-product-section', PdpProductSection);
}
